import React, {useEffect, useState} from 'react';
import {Box, Button, Flex, FormControl, FormLabel, Grid, Switch} from '@chakra-ui/react';
import {AdminListPageLayout} from '../../components/admin-list-page-layout/admin-list-page-layout';
import {BreadCrumbsComponent} from '../../components/breadcrumbs/breadcrumbsComponent';
import {useDispatch, useSelector} from 'react-redux';
import ConfigurationHistoryDrawer from './configuration-history-drawer';

import {
  fetchConfigDetail,
  listConfigurations,
  toggleConfigDrawer,
  toggleConfigDrawerContent
} from '../../actions/configurations';
import {ThunkDispatch} from 'redux-thunk';
import ConfigurationsTable from './ConfigurationsTable';
import {AnyAction} from 'redux';
import {useHistory, useParams} from 'react-router-dom';
import ConfigurationDetailDrawer from './configuration-detail-drawer';

const ConfigurationManagement = (props: any) => {
  const tenantId = props.tenantId || 'defaultTenantId';
  const tenantName = props.tenantName || tenantId;
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const history = useHistory();
  const { configId, orgId } = useParams<{
    configId?: string;
    orgId?: string;
  }>();

  const configurations: CMxAPI.Configuration[] = useSelector(
      (state: CMx.ApplicationState) => state.configurations.configurations
  );
  const configDetail: CMxAPI.Configuration[] | undefined = useSelector(
      (state: CMx.ApplicationState) => state.configurations.configurationDetail
  );
  const activeContext = useSelector(
      (state: CMx.ApplicationState) => state.ui.context.activeContext
  );
  const { serviceLines = [], organizationCode, organizationId } =
    activeContext || {};

  const isDrawerOpen = useSelector(
      (state: CMx.ApplicationState) => state.configurations.isDrawerOpen
  );

  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  const [showSystemConfigs, setShowSystemConfigs] = useState(false);

  // Determine if we should show the system config toggle
  const shouldShowSystemToggle = orgId === '1';

  // Determine the effective tenant ID based on toggle state
  const effectiveTenantId = shouldShowSystemToggle && showSystemConfigs ? '*' : tenantId;

  // Fetch the list of configurations on mount and when toggle changes
  useEffect(() => {
    dispatch(listConfigurations({ tenantId: effectiveTenantId }));
  }, [dispatch, effectiveTenantId]);

  // If there's a configId in the route, fetch its detail and open the drawer
  useEffect(() => {
    if (configId) {
      dispatch(fetchConfigDetail({ configurationId: configId, tenantId: effectiveTenantId }));
      dispatch(toggleConfigDrawer(true));
      dispatch(toggleConfigDrawerContent(true));
    }
  }, [configId, effectiveTenantId, dispatch]);

  // Close the drawer and navigate back to the configurations list
  const handleDrawerClose = () => {
    dispatch(toggleConfigDrawer(false));
    dispatch(toggleConfigDrawerContent(false));
    history.push(`/cmx/admin/orgs/${orgId}/configurations`);
  };

  // Handle system config toggle change
  const handleSystemToggleChange = (checked: boolean) => {
    setShowSystemConfigs(checked);
  };

  return (
      <AdminListPageLayout>
        <Grid flexShrink={0}>
          <Flex>
            <BreadCrumbsComponent />
          </Flex>
        </Grid>
        <Grid flex="1 1 0" minHeight={0} display="flex" flexDirection="column">
          <Box
              position="relative"
              flex="1 1 0"
              minHeight={0}
              display="flex"
              flexDirection="column">
            <Flex justify="space-between" align="center" mb={2} flexShrink={0}>
              <Box>
                {shouldShowSystemToggle && (
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="system-config-toggle" mb="0" fontSize="sm">
                        Show System-Level Configurations
                      </FormLabel>
                      <Switch
                          id="system-config-toggle"
                          isChecked={showSystemConfigs}
                          onChange={(e) => handleSystemToggleChange(e.target.checked)}
                          colorScheme="blue"
                          size="sm"
                      />
                    </FormControl>
                )}
              </Box>
              <Button
                  onClick={() => setIsHistoryDrawerOpen(true)}
                  colorScheme="blue"
                  variant="outline"
                  size="sm">
                View Change History
              </Button>
            </Flex>
            {activeContext && (
                <ConfigurationsTable
                    configurations={configurations}
                    serviceLines={serviceLines}
                    orgCode={orgId}
                    tenantId={effectiveTenantId}
                />
            )}
          </Box>
        </Grid>
        {configId && activeContext && (
            <ConfigurationDetailDrawer
                isOpen={isDrawerOpen}
                onClose={handleDrawerClose}
                configDetail={
                  configDetail && configDetail.length ? configDetail[0] : null
                }
                serviceLines={serviceLines}
                tenantId={effectiveTenantId}
            />
        )}

        {activeContext && (
            <ConfigurationHistoryDrawer
                isOpen={isHistoryDrawerOpen}
                onClose={() => setIsHistoryDrawerOpen(false)}
                tenantId={effectiveTenantId}
                tenantName={tenantName}
            />
        )}
      </AdminListPageLayout>
  );
};

export default ConfigurationManagement;
